import React, { useState, useEffect, useRef } from 'react'

import { useRouter } from 'next/router'

import useEnrollmentApi from '@hooks/useEnrollmentApi'
import useLookupData, { KEYS } from '@hooks/useLookupData'

import PageContent from '@components/layout/PageContent'
import {
  Alert,
  Text,
  DropdownGroup,
  DropdownOption,
  RadioGroup,
  ColorType,
  InputGroup,
  Box,
  Flex,
} from '@clear/design-system'
import { Stepper } from '@components/storybook'
import { enrollSteps } from '@utils/steps'
import {
  Actions,
  StepTitle,
  Form,
  Row,
  ErrorMessage,
} from '@components/layout/Form'

import { ContinueButton } from '@components/FormButtons'
import {
  citizenSupportingDocumentNotRequired,
  nonCitizenSupportingDocumentNotRequired,
} from '@utils/documentCheckCriteria'
import {
  DocumentCheckValues,
  DocumentCheckKeys,
  DocumentCheckLabels,
  documentCheckSchema,
} from '@validators/schema/enroll/documentCheckStepSchema'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useSessionTimer } from '@hooks/useSessionTimer'
import { extractErrors, setFormErrors } from '@utils/apiErrorMessages'
import { copyAndRemoveEmptyEntries } from '@api/enrollmentClient/utils'
import { getAccessToken, getXCLEARCorrelationInfo } from '@api/utils'
import Spinner from '@components/shared/Spinner'
import { Enrollment } from '@clear-denver/tpc-openapi-typescript'
import { DiscreteEvent, dispatchCustomEvent, KPI } from '@utils/customEvents'
import { Modal } from '@components/shared/Modal'
import FinePrint from '@components/shared/FinePrint'
import useGetLocales from '@hooks/useGetLocales'
import scrollToRef from '@utils/scrollToRef'
import { AlertVariantName } from '@clear/design-system/lib/theme/variants'
import { countriesWithSubdivisions } from '@utils/countries'
import { handleSelectPlan, userHasAccessToken } from '@utils/authentication'
import { PlanTypes, useEnrollmentContext } from '@components/EnrollmentContext'

export type DocumentCheckCache = {
  photoId: string
  citizenshipDocument: string
  namesOnDocumentsMatch: string
  birthCountry: string
  birthLocality: string
  birthSubdivision: string
  citizenshipCountry: string
  nonImmigrantVisaStatus: string
}

const radioGroupNameLinkingOptions: Array<DropdownOption> = [
  {
    label: 'Yes',
    value: 'true',
  },
  {
    label: 'No',
    value: 'false',
  },
]

function useDocumentDropdownOptions() {
  const [isLoading, setIsLoading] = useState(true)
  const [lookupData, isLoadingLookupData] = useLookupData([
    KEYS['us-citizen-photo-ids'],
    KEYS['non-us-born-citizenship-immigration-documents'],
    KEYS['non-us-citizen-photo-ids'],
    KEYS['us-born-citizenship-immigration-documents'],
  ])
  const [data, setData] = useState<any>()
  useEffect(() => {
    setIsLoading(true)
    if (!isLoadingLookupData) {
      setIsLoading(false)
      const citizen = Array.from(
        new Set(lookupData[KEYS['us-citizen-photo-ids']])
      )

      const nonCitizen = Array.from(
        new Set(lookupData[KEYS['non-us-citizen-photo-ids']])
      )
      const citizenNonUSBorn = Array.from(
        new Set(
          lookupData[KEYS['non-us-born-citizenship-immigration-documents']]
        )
      )
      const citizenUSBorn = Array.from(
        new Set(lookupData[KEYS['us-born-citizenship-immigration-documents']])
      )
      setData({
        photoId: {
          citizen,
          nonCitizen,
        },
        document: {
          citizenNonUSBorn,
          citizenUSBorn,
        },
      })
    }
  }, [isLoadingLookupData, lookupData])
  return [data, isLoading]
}

const defaultValues: DocumentCheckValues = {
  photoId: '',
  birthCountry: '',
  birthSubdivision: '',
  citizenshipCountry: '',
  nonImmigrantVisaStatus: '',
  birthLocality: '',
}

const lprOptions = [
  {
    value: 'lpr',
    label: 'Yes',
  },
  {
    value: 'ineligible',
    label: 'No',
  },
]

function getFormStateEnrollment(
  enrollment: Enrollment.Enrollment,
  defaultValues: DocumentCheckValues
): DocumentCheckValues {
  if (enrollment.documentCheck && enrollment.citizenship) {
    const { photoId, document, namesOnDocumentsMatch } =
      enrollment.documentCheck
    return {
      ...defaultValues,
      ...enrollment.citizenship,
      photoId,
      document: document || undefined,
      namesOnDocumentsMatch: namesOnDocumentsMatch || undefined,
    }
  }
  return defaultValues
}

const DocumentCheckPage = () => {
  const {
    client,
    state,
    documentCheckCache,
    updateState,
    clearState: clearEnrollmentState,
  } = useEnrollmentApi()
  const {
    register,
    handleSubmit,
    watch,
    formState,
    setError,
    clearErrors,
    unregister,
    setValue,
    trigger,
  } = useForm<DocumentCheckValues & { global: string }>({
    mode: 'onChange',
    resolver: yupResolver(documentCheckSchema),
    defaultValues: getFormStateEnrollment(state, defaultValues),
  })
  const { affiliate, biographicAuthInfo } = useEnrollmentContext()
  const countryOfBirth = biographicAuthInfo?.countryOfBirth
  useEffect(() => {
    if (countryOfBirth) {
      setValue('birthCountry', countryOfBirth)
    }
    if (userHasAccessToken() && !state.id) {
      setIsLoading(true)
      handleSelectPlan(
        getAccessToken(),
        client,
        PlanTypes.ENROLL,
        affiliate,
        updateState
      )
      setIsLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const { errors } = formState
  const router = useRouter()
  const { sessionTimeoutHandler } = useSessionTimer(clearEnrollmentState)
  const [showModal, setShowModal] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isListADisabled, setIsListADisabled] = useState(false)
  const [isListBDisabled, setIsListBDisabled] = useState(false)
  const [optionData, isLoadingOptionData] = useDocumentDropdownOptions()
  const [countries, getStatesByCountry] = useGetLocales()
  const { birthCountry, citizenshipCountry } = watch()
  const alertRef = useRef<HTMLDivElement>(null)

  const values = watch()

  const isUSCitizen = values.citizenshipCountry === 'US'
  const isUSBorn = values.birthCountry === 'US'
  const needsSubDivision = countriesWithSubdivisions.includes(
    birthCountry || ''
  )

  useEffect(() => {
    scrollToRef(alertRef)
  }, [errors.nonImmigrantVisaStatus])

  useEffect(() => {
    if (!!citizenshipCountry && citizenshipCountry !== 'US') {
      register('nonImmigrantVisaStatus')
    } else {
      unregister('nonImmigrantVisaStatus')
    }
  }, [citizenshipCountry, register, unregister])

  const getPhotoIdListADropdownData = () => {
    return isUSCitizen
      ? optionData?.photoId?.citizen.filter((option: DropdownOption) =>
          citizenSupportingDocumentNotRequired.includes(option.value)
        )
      : optionData?.photoId?.nonCitizen.filter((option: DropdownOption) =>
          nonCitizenSupportingDocumentNotRequired.includes(option.value)
        )
  }

  const getPhotoIdListBDropdownData = () => {
    return isUSCitizen
      ? optionData?.photoId?.citizen.filter(
          (option: DropdownOption) =>
            !citizenSupportingDocumentNotRequired.includes(option.value)
        )
      : optionData?.photoId?.nonCitizen.filter(
          (option: DropdownOption) =>
            !nonCitizenSupportingDocumentNotRequired.includes(option.value)
        )
  }

  const hideDocumentDropdown =
    !values.photoId ||
    (isUSCitizen &&
      citizenSupportingDocumentNotRequired.includes(values.photoId)) ||
    (!isUSCitizen &&
      nonCitizenSupportingDocumentNotRequired.includes(values.photoId))

  const getDocumentDropdownData = () => {
    if (!values.photoId) return []
    if (isLoadingOptionData) return []
    if (isUSCitizen && isUSBorn) {
      return optionData.document.citizenUSBorn
    }
    if (isUSCitizen && !isUSBorn) {
      return optionData.document.citizenNonUSBorn
    }
    return []
  }

  useEffect(() => {
    if (hideDocumentDropdown) {
      unregister('document')
      unregister('namesOnDocumentsMatch')
    }
  }, [hideDocumentDropdown, unregister])

  const onSubmit = async (formData: DocumentCheckValues) => {
    clearErrors('global')
    setIsLoading(true)
    const xCLEARCorrelationInfo = getXCLEARCorrelationInfo()
    const baseMetaData = {
      kpi: KPI.USER_INTERACTION,
      xCLEARCorrelationInfo,
      url: router.route,
    }
    try {
      const preDocumentCheckStep = {
        ...copyAndRemoveEmptyEntries(formData),
        namesOnDocumentsMatch:
          formData.namesOnDocumentsMatch !== undefined
            ? formData.namesOnDocumentsMatch
            : undefined,
      }
      await client.reportCitizenship({
        xCLEARCorrelationInfo,
        enrollmentId: state.id,
        preCitizenshipStep: {
          birthCountry: formData.birthCountry,
          birthLocality: formData.birthLocality,
          birthSubdivision: formData.birthSubdivision,
          citizenshipCountry: formData.citizenshipCountry,
          nonImmigrantVisaStatus: formData.nonImmigrantVisaStatus,
        },
      })

      dispatchCustomEvent(Enrollment.LogEntryLevelEnum.INFO, {
        payload: {
          message: 'Submit document check begin',
        },
        metadata: {
          ...baseMetaData,
          event: DiscreteEvent.DOCUMENT_CHECK_SUBMIT_BEGIN,
        },
      })
      const response = await client.chooseDocuments({
        xCLEARCorrelationInfo,
        enrollmentId: state.id,
        preDocumentCheckStep,
      })
      updateState(response)

      router.push('/enroll/you')
    } catch (error) {
      dispatchCustomEvent(Enrollment.LogEntryLevelEnum.ERROR, {
        payload: {
          message: error?.message,
          stack: error?.stack,
        },
        metadata: {
          event: DiscreteEvent.DOCUMENT_CHECK_SUBMIT_FAILURE,
          ...baseMetaData,
        },
      })
      if (error.status === 401) {
        sessionTimeoutHandler()
      }
      setFormErrors(await extractErrors(error, formData), setError)
    }
    setIsLoading(false)
  }

  useEffect(() => {
    if (values.document === 'BC') {
      setShowModal(true)
    }
  }, [values.document])

  const modalBody = (
    <>
      <div className="font-bold">Has all required fields:</div>
      <ul className="list-disc p-4 space-y-2">
        <li>Name, date of birth, place of birth</li>
        <li>Issuing city, county, or state</li>
        <li>Date filed with registrar's office</li>
        <li>Parents' names field</li>
        <li>
          Signature of the city, county, or state official registrar (not a
          church or religious entity)
        </li>
      </ul>
      <div className="font-bold pt-2 pb-4">
        First and last name match the Photo ID EXACTLY. If names are not an
        exact match, you MUST include a name-linking document
      </div>
      <div className="font-bold">Is NOT:</div>
      <ul className="list-disc p-4 space-y-2">
        <li>Birth document titled "Registration" or "Notification"</li>
        <li>Private hospital-issued document (even with a seal)</li>
        <li>Foreign birth document</li>
        <li>"No Record Certification of Birth" document</li>
        <li>Puerto Rico birth certificate issued before July 1, 2010</li>
        <li>Illegible due to wear/tear/damage/scan quality</li>
        <li>A photocopy</li>
      </ul>
    </>
  )
  const label = (
    <div>
      Required Documentation
      <br />
      <strong>
        Please choose one of the documents from List A. If you do not have a
        document from List A, select a document from List B.
      </strong>
    </div>
  )

  const handleSelect = (value: string, list: 'A' | 'B') => {
    if (value === '') {
      setIsListADisabled(false)
      setIsListBDisabled(false)
      setValue('photoId', value)
      trigger('photoId')
      return
    }
    if (list === 'A') {
      setIsListBDisabled(true)
      setIsListADisabled(false)
    } else {
      setIsListBDisabled(false)
      setIsListADisabled(true)
    }
    setValue('photoId', value)
    trigger('photoId')
  }
  return (
    <PageContent>
      <Stepper current={0} percent={0} steps={enrollSteps} />
      <StepTitle mb={5}>Citizenship and Required Documentation</StepTitle>
      <Alert
        mb={5}
        defaultIcon
        backgroundColor={'brandDarkBlue' as ColorType}
        color="neutralsWhite"
        text="Tip: The best document to use is a valid, unexpired U.S. Passport"
      />
      {errors.nonImmigrantVisaStatus && (
        <Box ref={alertRef}>
          <Alert
            variant={'error' as AlertVariantName}
            defaultIcon
            text="Only U.S. citizens, U.S. Nationals and Lawful Permanent Residents are eligible for TSA PreCheck&reg;."
          />
        </Box>
      )}

      <Form mt={4}>
        <Row>
          <DropdownGroup
            data-cy="birthCountry"
            label={DocumentCheckLabels.birthCountry}
            placeholder={DocumentCheckLabels.birthCountry}
            options={countries}
            {...register('birthCountry')}
            error={errors.birthCountry?.message}
            autoComplete="country"
          />
          <InputGroup
            label={DocumentCheckLabels.birthLocality}
            data-cy="birthLocality"
            placeholder={DocumentCheckLabels.birthLocality}
            {...register('birthLocality')}
            error={errors.birthLocality?.message}
            autoComplete="address-level2"
          />
        </Row>
        <Row>
          <DropdownGroup
            data-cy="birthSubdivision"
            label={DocumentCheckLabels.birthSubdivision}
            placeholder={DocumentCheckLabels.birthSubdivision}
            disabled={!birthCountry || !needsSubDivision}
            options={getStatesByCountry(birthCountry) || []}
            {...register('birthSubdivision')}
            error={errors.birthSubdivision?.message}
            autoComplete="address-level1"
          />
          <DropdownGroup
            data-cy="citizenshipCountry"
            label={DocumentCheckLabels.citizenshipCountry}
            placeholder={DocumentCheckLabels.citizenshipCountry}
            options={countries}
            {...register('citizenshipCountry')}
            error={errors.citizenshipCountry?.message}
            autoComplete="country"
          />
        </Row>
        {citizenshipCountry && citizenshipCountry !== 'US' && (
          <Flex
            flexDirection={['column', 'row']}
            justifyContent={['flex-start', 'space-between']}
            alignItems={[null, 'center']}
            my={4}
          >
            <Text>{DocumentCheckLabels.nonImmigrantVisaStatus}</Text>
            <RadioGroup
              containerProps={{
                flexDirection: 'row',
              }}
              {...register('nonImmigrantVisaStatus', {
                shouldUnregister: true,
              })}
              variant="default"
              options={lprOptions}
              selected={
                defaultValues?.nonImmigrantVisaStatus?.toLowerCase() ||
                undefined
              }
            />
          </Flex>
        )}
        <Row mx={0} my={4}>
          {label}
        </Row>
        <Row>
          <DropdownGroup
            data-cy="documentCheckPhotoId"
            onChange={(e) => handleSelect(e.target.value, 'A')}
            label="List A"
            aria-label="List A"
            placeholder={DocumentCheckLabels.photoId}
            value={values.photoId || documentCheckCache?.photoId}
            options={getPhotoIdListADropdownData()}
            disabled={
              !values.citizenshipCountry ||
              !values.birthCountry ||
              isListADisabled
            }
            error={errors[DocumentCheckKeys.PHOTO_ID]?.message}
          />
          <div className="font-bold m-auto">OR</div>
          <DropdownGroup
            data-cy="documentCheckPhotoId"
            aria-label="List B"
            label="List B"
            onChange={(e) => handleSelect(e.target.value, 'B')}
            placeholder={DocumentCheckLabels.photoId}
            value={values.photoId || documentCheckCache?.photoId}
            options={getPhotoIdListBDropdownData()}
            disabled={
              !values.citizenshipCountry ||
              !values.birthCountry ||
              isListBDisabled
            }
            error={errors[DocumentCheckKeys.PHOTO_ID]?.message}
          />
        </Row>

        {!hideDocumentDropdown && (
          <Row>
            <DropdownGroup
              data-cy="documentCheckCitizenship"
              {...register('document')}
              label="Choose a Citizenship/Immigration Document"
              placeholder={DocumentCheckLabels.document}
              value={values.document || documentCheckCache?.citizenshipDocument}
              options={getDocumentDropdownData()}
              error={errors[DocumentCheckKeys.DOCUMENT]?.message}
            />
          </Row>
        )}

        {!hideDocumentDropdown && (
          <>
            <Row
              flexDirection={['column', 'row']}
              justifyContent={['flex-start', 'space-between']}
              alignItems={['center']}
              p={4}
            >
              <Text variant="body">
                Does the name on the Photo ID and Citizenship/Immigration
                documents match?
              </Text>
              <RadioGroup
                {...register('namesOnDocumentsMatch')}
                data-cy="documentCheckNamesMatch"
                options={radioGroupNameLinkingOptions}
                containerProps={{
                  flexDirection: 'row',
                }}
                selected={
                  `${documentCheckCache?.namesOnDocumentsMatch}` ||
                  `${values.namesOnDocumentsMatch}`
                }
              />
            </Row>
            {`${values?.namesOnDocumentsMatch}` === 'false' ? (
              <Text variant="body">
                If you have legally changed your name, you must provide an
                original or certified name change document (such as marriage
                certificates and divorce decrees) in addition to the required
                documentation.
              </Text>
            ) : null}
          </>
        )}
      </Form>
      <FinePrint mt={4} />
      <Actions justifyContent="flex-end">
        <ContinueButton
          disabled={!formState.isValid}
          onClick={handleSubmit(onSubmit)}
          data-cy="continue"
        />
      </Actions>
      {isLoading && <Spinner />}
      {errors.global && <ErrorMessage>{errors.global.message}</ErrorMessage>}
      <Modal
        showModal={showModal}
        setShowModal={setShowModal}
        body={modalBody}
        header="Please confirm your birth certificate meets ALL requirements"
        canClose={true}
        successButtonText="Confirm"
        successClick={() => setShowModal(false)}
      />
    </PageContent>
  )
}

export default DocumentCheckPage
